import React, { useState, createContext, useContext, ReactNode } from 'react';
import {
  IconButton,
  Tooltip,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Backdrop,
  Paper,
  Fade,
} from '@mui/material';
import {
  Info as InfoIcon,
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Help content structure
export interface HelpContent {
  elementId: string;
  title: string;
  description: string;
  learnMoreUrl?: string;
  videoUrl?: string;
}

// Context for managing help mode state
interface HelpContextType {
  helpMode: boolean;
  toggleHelpMode: () => void;
  showHelp: (content: HelpContent) => void;
}

const HelpContext = createContext<HelpContextType>({
  helpMode: false,
  toggleHelpMode: () => {},
  showHelp: () => {},
});

export const useHelp = () => useContext(HelpContext);

// Help Provider Component
interface HelpProviderProps {
  children: ReactNode;
}

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
  const [helpMode, setHelpMode] = useState(false);
  const [selectedHelp, setSelectedHelp] = useState<HelpContent | null>(null);

  const toggleHelpMode = () => {
    setHelpMode((prev) => !prev);
  };

  const showHelp = (content: HelpContent) => {
    setSelectedHelp(content);
  };

  const closeHelp = () => {
    setSelectedHelp(null);
  };

  return (
    <HelpContext.Provider value={{ helpMode, toggleHelpMode, showHelp }}>
      {children}
      
      {/* "What's This?" mode overlay */}
      {helpMode && (
        <Backdrop
          open={helpMode}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            cursor: 'help',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              position: 'fixed',
              top: 16,
              right: 16,
              p: 2,
              maxWidth: 300,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={600}>
                "What's This?" Mode Active
              </Typography>
              <IconButton size="small" onClick={toggleHelpMode}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Click on any element to see its explanation
            </Typography>
          </Paper>
        </Backdrop>
      )}

      {/* Help content dialog */}
      <Dialog
        open={!!selectedHelp}
        onClose={closeHelp}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
      >
        {selectedHelp && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" fontWeight={600}>
                  {selectedHelp.title}
                </Typography>
                <IconButton size="small" onClick={closeHelp}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" color="text.primary" paragraph>
                {selectedHelp.description}
              </Typography>
              {selectedHelp.learnMoreUrl && (
                <Link
                  href={selectedHelp.learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'block', mt: 2 }}
                >
                  Learn More →
                </Link>
              )}
              {selectedHelp.videoUrl && (
                <Link
                  href={selectedHelp.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'block', mt: 1 }}
                >
                  Watch Video Tutorial →
                </Link>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeHelp} variant="contained">
                Got it
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </HelpContext.Provider>
  );
};

// Help Icon Component
interface HelpIconProps {
  content: HelpContent;
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end';
  size?: 'small' | 'medium';
}

export const HelpIcon: React.FC<HelpIconProps> = ({
  content,
  placement = 'top',
  size = 'small',
}) => {
  const { helpMode, showHelp } = useHelp();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    showHelp(content);
  };

  return (
    <Tooltip
      title={helpMode ? 'Click for help' : content.title}
      placement={placement}
      arrow
    >
      <IconButton
        size={size}
        onClick={handleClick}
        sx={{
          color: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.light',
          },
        }}
      >
        <InfoIcon fontSize={size} />
      </IconButton>
    </Tooltip>
  );
};

// "What's This?" Mode Toggle Button
interface WhatIsThisToggleProps {
  variant?: 'icon' | 'button';
}

export const WhatIsThisToggle: React.FC<WhatIsThisToggleProps> = ({
  variant = 'icon',
}) => {
  const { helpMode, toggleHelpMode } = useHelp();

  if (variant === 'button') {
    return (
      <Button
        variant={helpMode ? 'contained' : 'outlined'}
        startIcon={<HelpOutlineIcon />}
        onClick={toggleHelpMode}
        size="small"
      >
        {helpMode ? 'Exit Help Mode' : "What's This?"}
      </Button>
    );
  }

  return (
    <Tooltip title={helpMode ? 'Exit Help Mode' : "What's This? Mode"} arrow>
      <IconButton
        onClick={toggleHelpMode}
        color={helpMode ? 'primary' : 'default'}
        sx={{
          backgroundColor: helpMode ? 'primary.light' : 'transparent',
        }}
      >
        <HelpOutlineIcon />
      </IconButton>
    </Tooltip>
  );
};

// Inline Help Text Component
interface InlineHelpTextProps {
  text: string;
}

export const InlineHelpText: React.FC<InlineHelpTextProps> = ({ text }) => {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: 'block',
        mt: 0.5,
        fontSize: '0.75rem',
        lineHeight: 1.4,
      }}
    >
      {text}
    </Typography>
  );
};

// Clickable Help Element (for "What's This?" mode)
interface HelpElementProps {
  content: HelpContent;
  children: ReactNode;
}

export const HelpElement: React.FC<HelpElementProps> = ({ content, children }) => {
  const { helpMode, showHelp } = useHelp();

  const handleClick = (e: React.MouseEvent) => {
    if (helpMode) {
      e.stopPropagation();
      e.preventDefault();
      showHelp(content);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: helpMode ? 'help' : 'default',
        position: 'relative',
        '&:hover': helpMode
          ? {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            }
          : {},
      }}
    >
      {children}
    </Box>
  );
};
